'use client';

import { useState, useEffect, useRef } from 'react';
import { BsX, BsRobot, BsLightningCharge, BsStars, BsCheck, BsExclamationTriangle } from 'react-icons/bs';
import { motion, AnimatePresence } from 'framer-motion';

export default function AgentModal({ 
  isOpen, 
  onClose, 
  agentType = 'ci-cd',
  title,
  children,
  size = 'md',
  type = 'default' // 'default', 'success', 'warning', 'error'
}) {
  const [isVisible, setIsVisible] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 200);
  };

  // Get agent-specific theme colors based on agent type
  const getAgentTheme = () => {
    const themes = {
      "ci-cd": "from-blue-600 via-indigo-600 to-indigo-700",
      "cloud-infrastructure": "from-sky-500 via-blue-600 to-blue-700",
      "code-analysis": "from-indigo-600 via-purple-600 to-purple-700",
      "security-scanning": "from-red-500 via-red-600 to-red-700",
      "container-orchestration": "from-cyan-500 via-blue-600 to-blue-700",
      "performance-monitoring": "from-emerald-500 via-teal-600 to-teal-700",
      "load-testing": "from-amber-500 via-orange-500 to-orange-600",
      "incident-response": "from-rose-500 via-red-600 to-red-700",
    };
    
    return themes[agentType] || "from-indigo-600 via-purple-600 to-blue-700";
  };

  // Get modal size classes
  const getSizeClasses = () => {
    const sizes = {
      sm: 'max-w-md',
      md: 'max-w-lg',
      lg: 'max-w-2xl',
      xl: 'max-w-4xl',
      full: 'max-w-7xl'
    };
    return sizes[size] || sizes.md;
  };

  // Get type-specific icon and colors
  const getTypeConfig = () => {
    const configs = {
      default: {
        icon: BsRobot,
        iconColor: 'text-white',
        bgColor: 'bg-gray-100'
      },
      success: {
        icon: BsCheck,
        iconColor: 'text-white',
        bgColor: 'bg-green-100'
      },
      warning: {
        icon: BsExclamationTriangle,
        iconColor: 'text-white',
        bgColor: 'bg-yellow-100'
      },
      error: {
        icon: BsExclamationTriangle,
        iconColor: 'text-white',
        bgColor: 'bg-red-100'
      }
    };
    return configs[type] || configs.default;
  };

  const agentTheme = getAgentTheme();
  const sizeClasses = getSizeClasses();
  const typeConfig = getTypeConfig();
  const IconComponent = typeConfig.icon;

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const modalVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.95,
      y: -20
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.95,
      y: -20,
      transition: {
        duration: 0.2
      }
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.2 }}
          />
          
          {/* Modal Container */}
          <div className="flex min-h-full items-center justify-center p-4">
            <motion.div
              ref={modalRef}
              className={`relative w-full ${sizeClasses} bg-white rounded-xl shadow-2xl overflow-hidden`}
              variants={modalVariants}
              initial="hidden"
              animate={isVisible ? "visible" : "hidden"}
              exit="exit"
            >
              {/* Enhanced Header with Gradient */}
              <div className={`bg-gradient-to-r ${agentTheme} p-5 text-white relative`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="relative">
                      <IconComponent size={20} className={`mr-2 ${typeConfig.iconColor}`} />
                      <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-400 rounded-full">
                        <span className="absolute inset-0 w-full h-full bg-green-400 rounded-full animate-ping opacity-75"></span>
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold flex items-center text-lg">
                        {title || agentType.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                        <BsLightningCharge className="ml-2 text-yellow-300" size={16} />
                      </h3>
                      <div className="text-xs text-white/80 flex items-center mt-1">
                        <span className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1.5"></span>
                        Powered by AI
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center bg-white/10 rounded-full px-2 py-0.5 text-xs backdrop-blur-sm">
                      <BsStars className="mr-1 text-yellow-300" size={10} />
                      AI Enhanced
                    </div>
                    <motion.button
                      onClick={handleClose}
                      className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <BsX size={20} className="text-white" />
                    </motion.button>
                  </div>
                </div>
                
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-16 translate-x-16"></div>
                <div className="absolute bottom-0 left-0 w-20 h-20 bg-white/5 rounded-full translate-y-10 -translate-x-10"></div>
              </div>

              {/* Modal Content */}
              <div className="p-6 bg-gradient-to-b from-gray-50 to-white min-h-[200px]">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.3 }}
                >
                  {children}
                </motion.div>
              </div>

              {/* Optional Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                <div className="flex justify-between items-center">
                  <div className="text-xs text-gray-500">
                    AI-powered Talent Repo environment
                  </div>
                  <div className="flex space-x-2">
                    <motion.button
                      onClick={handleClose}
                      className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Close
                    </motion.button>
                    <motion.button
                      className={`px-4 py-2 text-sm bg-gradient-to-r ${agentTheme} text-white rounded-md hover:opacity-90 transition-opacity`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Confirm
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
}

// Example usage component
export function ExampleModalUsage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentAgent, setCurrentAgent] = useState('ci-cd');
  const [modalType, setModalType] = useState('default');

  const agentTypes = [
    'ci-cd',
    'cloud-infrastructure', 
    'code-analysis',
    'security-scanning',
    'container-orchestration',
    'performance-monitoring',
    'load-testing',
    'incident-response'
  ];

  return (
    <div className="p-8 min-h-screen bg-gray-100">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">
          AgentModal Demo
        </h1>
        
        {/* Controls */}
        <div className="bg-white p-6 rounded-xl shadow-sm mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Agent Type
              </label>
              <select 
                value={currentAgent} 
                onChange={(e) => setCurrentAgent(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                {agentTypes.map(type => (
                  <option key={type} value={type}>
                    {type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Modal Type
              </label>
              <select 
                value={modalType} 
                onChange={(e) => setModalType(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="default">Default</option>
                <option value="success">Success</option>
                <option value="warning">Warning</option>
                <option value="error">Error</option>
              </select>
            </div>
            
            <div className="flex items-end">
              <motion.button
                onClick={() => setIsModalOpen(true)}
                className="w-full px-4 py-2 bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-700 text-white rounded-md hover:opacity-90 transition-opacity"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Open Modal
              </motion.button>
            </div>
          </div>
        </div>

        {/* Modal */}
        <AgentModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          agentType={currentAgent}
          title={`${currentAgent.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')} Configuration`}
          size="lg"
          type={modalType}
        >
          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-semibold text-gray-800 mb-3">
                Agent Configuration Panel
              </h4>
              <p className="text-gray-600 mb-4">
                Configure your {currentAgent.split('-').join(' ')} agent settings and preferences. 
                This modal maintains the same design language as the chat component with beautiful gradients and animations.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <h5 className="font-medium text-gray-800 mb-2 flex items-center">
                  <BsRobot className="mr-2 text-indigo-600" size={16} />
                  AI Settings
                </h5>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Auto-response</span>
                    <div className="w-10 h-5 bg-indigo-600 rounded-full flex items-center p-1">
                      <div className="w-3 h-3 bg-white rounded-full transform translate-x-4 transition-transform"></div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Smart analysis</span>
                    <div className="w-10 h-5 bg-gray-300 rounded-full flex items-center p-1">
                      <div className="w-3 h-3 bg-white rounded-full transition-transform"></div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <h5 className="font-medium text-gray-800 mb-2 flex items-center">
                  <BsLightningCharge className="mr-2 text-yellow-500" size={16} />
                  Performance
                </h5>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Response Speed</span>
                      <span className="text-indigo-600">85%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-gradient-to-r from-indigo-500 to-purple-500 h-2 rounded-full" style={{width: '85%'}}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Accuracy</span>
                      <span className="text-emerald-600">92%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 h-2 rounded-full" style={{width: '92%'}}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-4 rounded-lg border border-indigo-200">
              <div className="flex items-start">
                <BsStars className="text-indigo-600 mr-3 mt-1" size={16} />
                <div>
                  <h5 className="font-medium text-indigo-900 mb-1">AI Enhancement Active</h5>
                  <p className="text-sm text-indigo-700">
                    Your agent is powered by advanced AI capabilities with real-time learning and adaptation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </AgentModal>
      </div>
    </div>
  );
}